import { useEffect, useRef, useState, type RefObject } from 'react'
import { isPremiumSection } from '../domain/premium'
import { Icon } from './Icon'

/**
 * The quick-jump bar, which follows the reader down the report.
 *
 * There are around nineteen cards and the bar is wider than any screen, so on its own it shows the
 * first six and hides the rest behind a sideways scroll nobody performs. By the time someone is
 * reading the timeline, the bar above them is still offering the summary and the schade card: it
 * has stopped describing where they are and become a list of places they have already been.
 *
 * So the current card marks itself and the bar scrolls itself to match. That does two jobs at once,
 * and the second is the one worth protecting: the bar is the only thing on screen that says how
 * much report is left, and a reader who can see "Milieuzones, Waarde-informatie, Kosten" ahead of
 * the chip they are on knows there is more below without scrolling to find out.
 */
export function SectionNav({ sections }: { sections: { id: string; title: string }[] }) {
  const navRef = useRef<HTMLElement>(null)
  // At the very top of the report no card has reached the reading line yet, and an unmarked bar
  // reads as a feature that failed rather than as "you have not started". The first chip is where
  // the reader is about to be, and marking it costs no movement: it is already at scroll zero.
  const active = useActiveSection(sections.map((section) => section.id).join('|')) ?? sections[0]?.id

  useNavFollowsActive(navRef, active)

  return (
    <nav className="section-nav" aria-label="Onderdelen van dit rapport" ref={navRef}>
      {sections.map((section) => (
        <a
          className={`section-nav__link${isPremiumSection(section.id) ? ' section-nav__link--premium' : ''}`}
          href={`#${section.id}`}
          key={section.id}
          // The hook scrolls the bar by looking its chip up, so the id has to be on the element as
          // well as in the href: a href carries a `#` and is not a selector.
          data-section={section.id}
          aria-current={section.id === active ? 'location' : undefined}
        >
          {isPremiumSection(section.id) && <Icon name="unlock" size={12} />}
          {section.title}
        </a>
      ))}
    </nav>
  )
}

/**
 * The top of the band a card has to cross to count as the one being read, in pixels.
 *
 * Read from `.card`'s computed `scroll-margin-top` rather than written down here, because it has to
 * be the same figure: a chip scrolls its card to exactly this line, so a card resting there is the
 * card whose chip should be marked. It also changes with the width — the site header is pinned only
 * where it fits on one row — and a constant here would be right at one width and wrong at the rest.
 */
function readingLine(card: HTMLElement): number {
  return Number.parseFloat(getComputedStyle(card).scrollMarginTop) || 0
}

/** The foot of the band, as a fraction of the viewport. */
const BAND_BOTTOM_FRACTION = 0.45

/**
 * Which card the reader is on, as an id.
 *
 * The band runs from the reading line down to a little under halfway, and the topmost card
 * overlapping it wins. A band rather than a line because the cards here differ in height by two
 * orders of magnitude — the APK card runs to thousands of pixels, "Milieuzones" to a couple of
 * hundred — and any single-pixel test flickers between neighbours on the short ones.
 *
 * Nothing is marked while the band is empty, which happens in the gap between two cards: the last
 * answer stands rather than being cleared, because a bar that unmarks itself between every card
 * reads as broken.
 *
 * **The observer is only the trigger. The answer is measured fresh.** Its entries are deliberately
 * ignored rather than accumulated into a set of what is currently on screen, because that set has
 * to survive the observer being torn down and rebuilt — which happens here whenever the section
 * list changes, and `zeldzaamheid` and `modelgebreken` are inserted seconds after everything else.
 * A set carried across that boundary is state to keep correct for no gain: `getBoundingClientRect`
 * in the callback is one pass over a couple of dozen elements, runs only when something crossed an
 * edge, and cannot disagree with what is actually on the page. The zero-height guard goes with it,
 * for an element observed in the frame it was inserted.
 *
 * `ids` arrives as one joined string so the effect can depend on it directly. That the list changes
 * after the first render is the point, not an accident: the two late cards have to be picked up.
 *
 * The observer's top margin is the reading line, so it is rebuilt when a resize moves that line.
 * iOS fires `resize` whenever the address bar collapses, so `connect` returns early when nothing
 * changed.
 */
function useActiveSection(ids: string): string | undefined {
  const [active, setActive] = useState<string>()

  useEffect(() => {
    const cards = ids
      .split('|')
      .map((id) => document.getElementById(id))
      .filter((card): card is HTMLElement => card !== null)

    const first = cards[0]

    if (!first) {
      return
    }

    let line = -1
    let observer: IntersectionObserver | undefined

    const update = () => {
      const bandBottom = window.innerHeight * BAND_BOTTOM_FRACTION

      const current = cards.find((card) => {
        const box = card.getBoundingClientRect()

        return box.height > 0 && box.bottom > line && box.top < bandBottom
      })

      if (current) {
        setActive(current.id)
      }
    }

    const connect = () => {
      const next = readingLine(first)

      if (next === line) {
        return
      }

      line = next
      observer?.disconnect()
      observer = new IntersectionObserver(update, {
        rootMargin: `-${line}px 0px -${Math.round((1 - BAND_BOTTOM_FRACTION) * 100)}% 0px`,
      })

      for (const card of cards) {
        observer.observe(card)
      }
    }

    connect()
    window.addEventListener('resize', connect)

    return () => {
      window.removeEventListener('resize', connect)
      observer?.disconnect()
    }
  }, [ids])

  return active
}

/**
 * Brings the marked chip into view, by moving the bar and nothing else.
 *
 * **Never `scrollIntoView` here.** It scrolls every ancestor scrolling box, the document included,
 * and for content inside a `position: sticky` element the browser aims at the element's *layout*
 * position, which for this bar is near the top of the report. The result is that reading downwards
 * yanks the page back upwards, animated, because `html` sets `scroll-behavior: smooth`. It is the
 * same trap `applyMask` works around in the header's search field, arriving from the other
 * direction. Writing `scrollLeft` touches this element's scrolling box alone.
 *
 * Whether that write animates is left to CSS: `.section-nav` sets `scroll-behavior: smooth` and the
 * reduced-motion block turns it off, so there is no media query to read here.
 */
function useNavFollowsActive(navRef: RefObject<HTMLElement | null>, active: string | undefined) {
  useEffect(() => {
    const nav = navRef.current

    if (!nav || !active) {
      return
    }

    // On a screen wide enough to show every chip there is nothing to follow, and moving a bar that
    // fits would be motion for its own sake.
    const overflow = nav.scrollWidth - nav.clientWidth

    if (overflow <= 0) {
      return
    }

    const chip = nav.querySelector<HTMLElement>(`[data-section="${CSS.escape(active)}"]`)

    if (!chip) {
      return
    }

    // Centred rather than merely scrolled into view, so the chips on either side are visible: what
    // is coming next is as much of the answer as where you are.
    const centred = chip.offsetLeft - (nav.clientWidth - chip.offsetWidth) / 2

    nav.scrollLeft = Math.max(0, Math.min(centred, overflow))
  }, [navRef, active])
}
